const DESKTOP_QUERY = '(min-width: 750px)';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

class DarkrimProductSlider extends HTMLElement {
  connectedCallback() {
    this.slideshow = this.querySelector('slideshow-component');
    this.progress = this.querySelector('[data-darkrim-progress]');
    this.progressFill = this.querySelector('[data-darkrim-progress-fill]');
    this.previousButton = this.querySelector('[data-darkrim-previous]');
    this.nextButton = this.querySelector('[data-darkrim-next]');
    this.total = Number(this.dataset.slideCount) || 0;
    this.desktopMediaQuery = window.matchMedia(DESKTOP_QUERY);
    this.reducedMotionQuery = window.matchMedia(REDUCED_MOTION_QUERY);

    this.addEventListener('slideshow:select', this.handleSlideSelect);
    this.addEventListener('shopify:block:select', this.handleBlockSelect);
    this.desktopMediaQuery.addEventListener('change', this.handleViewportChange);
    this.reducedMotionQuery.addEventListener('change', this.handlePlaybackPreferenceChange);
    document.addEventListener('visibilitychange', this.handlePlaybackPreferenceChange);
    this.updateProgress(0);
    this.syncActiveVideo();
  }

  disconnectedCallback() {
    this.removeEventListener('slideshow:select', this.handleSlideSelect);
    this.removeEventListener('shopify:block:select', this.handleBlockSelect);
    this.desktopMediaQuery?.removeEventListener('change', this.handleViewportChange);
    this.reducedMotionQuery?.removeEventListener('change', this.handlePlaybackPreferenceChange);
    document.removeEventListener('visibilitychange', this.handlePlaybackPreferenceChange);
    this.querySelectorAll('[data-darkrim-prod-video-host]').forEach(this.clearVideoHost);
  }

  handleSlideSelect = (event) => {
    if (!this.slideshow?.contains(event.detail.slide)) return;
    this.updateProgress(event.detail.index);
    this.syncActiveVideo(event.detail.slide);
  };

  // Keep the carousel aligned with the block selected in Shopify's Theme Editor.
  handleBlockSelect = (event) => {
    if (!(event.target instanceof Element)) return;

    const slide = event.target.closest('slideshow-slide');
    const slides = Array.from(this.slideshow?.querySelectorAll('slideshow-slide') || []);
    const index = slides.indexOf(slide);

    if (index >= 0) this.slideshow?.select(index, undefined, { animate: false });
  };

  handleViewportChange = () => {
    this.syncActiveVideo();
  };

  handlePlaybackPreferenceChange = () => {
    this.syncActiveVideo();
  };

  findActiveSlide() {
    return this.querySelector("slideshow-slide[aria-hidden='false']");
  }

  syncActiveVideo(activeSlide = this.findActiveSlide()) {
    if (!(activeSlide instanceof HTMLElement)) return;

    const viewport = this.desktopMediaQuery.matches ? 'pc' : 'mobile';

    this.querySelectorAll('[data-darkrim-prod-video-host]').forEach((host) => {
      if (!(host instanceof HTMLElement)) return;

      const mediaFrame = host.closest('[data-darkrim-prod-media-frame]');
      const slide = host.closest('slideshow-slide');
      const shouldHydrate = slide === activeSlide && mediaFrame?.dataset[`${viewport}Mode`] === 'video';

      if (!shouldHydrate) {
        this.clearVideoHost(host);
        return;
      }

      let video = host.querySelector('video');

      if (host.dataset.viewport !== viewport || !(video instanceof HTMLVideoElement)) {
        const template = mediaFrame.querySelector(`[data-darkrim-prod-video-template="${viewport}"]`);
        if (!(template instanceof HTMLTemplateElement)) {
          this.clearVideoHost(host);
          return;
        }

        const fragment = template.content.cloneNode(true);
        video = fragment.querySelector('video');
        if (!(video instanceof HTMLVideoElement)) return;

        video.setAttribute('aria-hidden', 'true');
        video.tabIndex = -1;
        video.muted = true;
        video.playsInline = true;
        host.replaceChildren(fragment);
        host.dataset.viewport = viewport;
      }

      this.syncVideoPlayback(video);
    });
  }

  clearVideoHost = (host) => {
    if (!(host instanceof HTMLElement)) return;

    host.querySelector('video')?.pause();
    host.replaceChildren();
    delete host.dataset.viewport;
  };

  syncVideoPlayback(video) {
    if (document.hidden || this.reducedMotionQuery.matches) {
      video.pause();
      return;
    }

    video.play().catch(() => {
      // Autoplay can be denied by browser policy; the selected video's poster remains visible.
    });
  }

  updateProgress(index) {
    if (!this.total || !this.progress || !this.progressFill) return;

    const current = Math.min(Math.max(index + 1, 1), this.total);
    const percentage = (current / this.total) * 100;

    this.progressFill.style.setProperty('--darkrim-prod-progress', `${percentage}%`);
    this.progress.setAttribute('aria-valuenow', String(current));
    this.progress.setAttribute('aria-valuetext', `${current} / ${this.total}`);

    if (this.previousButton) this.previousButton.disabled = current === 1;
    if (this.nextButton) this.nextButton.disabled = current === this.total;
  }
}

if (!customElements.get('darkrim-product-slider')) {
  customElements.define('darkrim-product-slider', DarkrimProductSlider);
}
